import {
  BlockedByRelationshipIcon,
  BlockingRelationshipIcon,
  ChildRelationshipIcon,
  DuplicatesRelationshipIcon,
  MergeIcon,
  ParentRelationshipIcon,
  RelatesToRelationshipIcon,
} from "~/icons";
import { CommandId, DropdownMenuItemKind, RelationshipDropdownKind, RelationshipKindKind } from "~/shared/enums";
import type { DropdownMenuItem } from "~/shared/types";

export type RelationshipDropdownItem = DropdownMenuItem & {
  relationshipKindKind: RelationshipKindKind;
  isForward?: boolean;
};

export const RELATIONSHIP_KIND_KIND_OPTIONS = Object.values(RelationshipKindKind);

export const ORDERED_RELATIONSHIP_CONFIGURATION = [
  {
    kind: RelationshipKindKind.PARENT_OF,
    isForward: false,
    label: "Parent",
    icon: ParentRelationshipIcon,
    singleselect: true,
  },
  {
    kind: RelationshipKindKind.BLOCKS,
    isForward: false,
    label: "Blocked by",
    icon: BlockedByRelationshipIcon,
  },
  {
    kind: RelationshipKindKind.BLOCKS,
    isForward: true,
    label: "Blocks",
    icon: BlockingRelationshipIcon,
  },
  {
    kind: RelationshipKindKind.DUPLICATES,
    label: "Duplicates",
    icon: DuplicatesRelationshipIcon,
  },
  {
    kind: RelationshipKindKind.RELATES_TO,
    label: "Relates to",
    icon: RelatesToRelationshipIcon,
  },
];

export const ORDERED_RELATIONSHIP_CONFIGURATION_WITH_SUBTASKS = [
  {
    kind: RelationshipKindKind.PARENT_OF,
    isForward: true,
    label: "Subtask",
    icon: ChildRelationshipIcon,
  },
  ...ORDERED_RELATIONSHIP_CONFIGURATION,
];

export const EXCLUDE_CURRENT_TASK_REASON = "This is the current task";

// Individual relationship dropdown items
export const CREATE_SUBTASK_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Add subtask",
  kind: DropdownMenuItemKind.BUTTON,
  icon: ChildRelationshipIcon,
  commandId: CommandId.CREATE_SUBTASKS,
  relationshipKindKind: RelationshipKindKind.PARENT_OF,
  isForward: true,
};

export const SET_SUBTASK_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Set existing subtask",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: ChildRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.PARENT_OF,
  isForward: true,
};

export const SET_PARENT_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Set parent",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: ParentRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.PARENT_OF,
  isForward: false,
};

export const BLOCKED_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Mark blocked",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: BlockedByRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.BLOCKS,
  isForward: false,
};

export const BLOCKING_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Mark blocking",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: BlockingRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.BLOCKS,
  isForward: true,
};

export const DUPLICATE_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Mark duplicated",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: DuplicatesRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.DUPLICATES,
};

export const MERGED_INTO_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Merge into other task",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: MergeIcon,
  relationshipKindKind: RelationshipKindKind.DUPLICATES,
};

export const RELATES_TO_DROPDOWN_ITEM: RelationshipDropdownItem = {
  title: "Reference another task",
  kind: DropdownMenuItemKind.COMPONENT,
  icon: RelatesToRelationshipIcon,
  relationshipKindKind: RelationshipKindKind.RELATES_TO,
};

// Grouped maps
export const SUBTASK_RELATIONSHIP_DROPDOWN_OPTIONS = new Map<RelationshipDropdownKind, RelationshipDropdownItem>([
  [RelationshipDropdownKind.CREATE_SUBTASK, CREATE_SUBTASK_DROPDOWN_ITEM],
  [RelationshipDropdownKind.SET_SUBTASK, SET_SUBTASK_DROPDOWN_ITEM],
  [RelationshipDropdownKind.SET_PARENT, SET_PARENT_DROPDOWN_ITEM],
]);

export const OTHER_RELATIONSHIP_DROPDOWN_OPTIONS = new Map<RelationshipDropdownKind, RelationshipDropdownItem>([
  [RelationshipDropdownKind.BLOCKED, BLOCKED_DROPDOWN_ITEM],
  [RelationshipDropdownKind.BLOCKING, BLOCKING_DROPDOWN_ITEM],
  [RelationshipDropdownKind.DUPLICATE, DUPLICATE_DROPDOWN_ITEM],
  [RelationshipDropdownKind.MERGED_INTO, MERGED_INTO_DROPDOWN_ITEM],
  [RelationshipDropdownKind.RELATES_TO, RELATES_TO_DROPDOWN_ITEM],
]);

// Combined maps for backwards compatibility
export const RELATIONSHIP_DROPDOWN_KIND_TO_DROPDOWN_OPTION = new Map<
  RelationshipDropdownKind,
  RelationshipDropdownItem
>([[RelationshipDropdownKind.SET_PARENT, SET_PARENT_DROPDOWN_ITEM], ...OTHER_RELATIONSHIP_DROPDOWN_OPTIONS]);

export const RELATIONSHIP_DROPDOWN_KIND_TO_DROPDOWN_OPTION_WITH_SUBTASKS = new Map<
  RelationshipDropdownKind,
  RelationshipDropdownItem
>([...SUBTASK_RELATIONSHIP_DROPDOWN_OPTIONS, ...OTHER_RELATIONSHIP_DROPDOWN_OPTIONS]);
